import React from 'react';
import PropTypes from 'prop-types';
import {Image} from '../../Image';
import {SliderGroup} from '../SharedComponents/SliderGroup';
import {Question} from '../SharedComponents/Question';
import {QuestionFooter} from '../SharedComponents/QuestionFooter';
import {SingleSelectOption} from '../SharedComponents/SingleSelectOption';
import {SingleSelectOptionItem} from '../SharedComponents/SingleSelectOptionItem';
import {QUESTION_CONTENTS} from '@learningblocks/exp-constants/lib/Utilities';
import {RenderHTML} from '@learningblocks/storybook-components';
import '../SharedComponents/CommonStyles';

const ALL_CORRECT_ANSWERS = -1;

/**
 * Experimental-Design Question component:
 *  - You will have n choices to select from
 *  - The question will have a single correct Answer.
 */
export function SingleSelect(props) {
  const checkCSCQHasUnsubmittedOptions = (optionList) => {
    const hasUnsubmittedOption = optionList.reduce((boolVal, option) => {
      if (option.disabled === false && option.selected === true) {
        boolVal = true;
      }
      return boolVal;
    }, false);
    return hasUnsubmittedOption === false;
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else {
      let optionList = [];
      if (props.options) optionList = props.options;
      if (props.typedOptions) optionList = props.typedOptions;
      return checkCSCQHasUnsubmittedOptions(optionList);
    }
  };

  const getIsCorrect = ({id, selected}) => {
    if (props.answerId === ALL_CORRECT_ANSWERS) {
      return selected;
    } else {
      return props.answerId === id;
    }
  };

  const selectedOption = ({selected, disabled}) =>
    !disabled && selected ? 'selected-color' : '';

  const renderOptions = (key, options) => {
    return options.map((option, id) => (
      <SingleSelectOption
        key={`${key}_${id}`}
        className={`${props.className}__option ${selectedOption(option)}`}
        componentID={props.id}
        isCorrect={getIsCorrect(option)}
        selectOption={props.selectOption}
        {...option}>
        <SingleSelectOptionItem {...option} />
      </SingleSelectOption>
    ));
  };

  const renderSliderGroups = (key, sliderGroups) => {
    return sliderGroups.map((slider, id) => (
      <SliderGroup key={`${key}_${id}`} {...slider} componentID={props.id} />
    ));
  };

  const renderImage = (key, image) => (
    <Image
      src={image}
      className={`${props.className}__image`}
      key={key}
      alt={props.altText}
      width='100%'
    />
  );

  const renderQuestion = (key, question) => (
    <Question
      key={key}
      className={`${props.className}__text`}
      question={question}
    />
  );

  const renderText = (key, text) => (
    <div key={key} className={`${props.className}__text`}>
      <RenderHTML content={text} />
    </div>
  );

  const renderByType = (type, key, content) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.IMAGE:
        return renderImage(key, content);
      case QUESTION_CONTENTS.OPTIONS:
      case QUESTION_CONTENTS.TYPED_OPTIONS:
        return renderOptions(key, content);
      case QUESTION_CONTENTS.SLIDER_GROUPS:
        return renderSliderGroups(key, content);
      case QUESTION_CONTENTS.CONTENT_LIST:
        return content.map((listItem, subKey) =>
          renderByType(listItem.type, `${key}_${subKey}`, listItem.content)
        );
      default:
        return null;
    }
  };

  return (
    <div className={props.className}>
      {props.content.map((type, key) => renderByType(type, key, props[type]))}
      <QuestionFooter
        className={`${props.className}__footer`}
        complete={props.complete}
        componentID={props.id}
        componentType={props.type}
        totalAttempts={props.totalAttempts}
        submitAnswer={props.submitAnswer}
        submitDisabled={setSubmitDisabled()}
      />
    </div>
  );
}

SingleSelect.propTypes = {
  /**
   * The prop for the ID of correct answer option [ -1 means all answers are correct]
   */
  answerId: PropTypes.number.isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  // className: PropTypes.string.isRequired,
  className: PropTypes.oneOfType([
    PropTypes.oneOf(['question', 'question_color']),
    PropTypes.string,
  ]).isRequired,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * List of static contents of type question, text, and image you want to display
   */
  contentList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Type of content Item
       */
      type: PropTypes.oneOf(['question', 'image', 'text']),
      /**
       * Content Item assicated with the type
       */
      content: PropTypes.string,
    })
  ),
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The prop that hold the url to the image,
   */
  image: PropTypes.string,
  /**
   * The prop that hold the array of answer options.
   */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**

  * The prop that holds the text for the question
   */
  question: PropTypes.string.isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(['CSCQ_COLOR', 'CSCQ']),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * The callback to be executed when an option is selected
   */
  selectOption: PropTypes.func.isRequired,
};

SingleSelect.defaultProps = {
  className: 'question',
  type: 'CSCQ',
  answerId: 0,
  complete: false,
  content: [],
  image: '',
  options: [],
  question: '',
  totalAttempts: 0,
};
